import qs from 'qs';

interface ExactMatchedPath {
  path: string;
  ignoreQuery: boolean;
}

interface SubDomainExactMatchedPath {
  subDomain: string;
  ignoreQuery: boolean;
  queries?: string[];
}

const REQUIRED_EXACT_MATCHING_PATH_LIST: ExactMatchedPath[] = [
  {
    path: '/accommodations',
    ignoreQuery: false,
  },
  {
    path: '/promotions',
    ignoreQuery: true,
  },
  {
    path: '/search',
    ignoreQuery: false,
  },
];

const REQUIRED_SUB_DOMAINS_EXACT_MATCHING_PATH_LIST: SubDomainExactMatchedPath[] = [
  {
    subDomain: 'accommodation',
    ignoreQuery: false,
    queries: ['isDomestic'],
  },
  {
    subDomain: 'flight',
    ignoreQuery: true,
  },
];

const ALLOW_SUB_DOMAINS = REQUIRED_SUB_DOMAINS_EXACT_MATCHING_PATH_LIST.map(
  ({ subDomain }) => subDomain,
);

/**
 * 항공 버티컬홈(/main/flights)의 routeType을 국내/해외로 정규화한다.
 * routeType이 없거나 'domestic'이 아니면 모두 해외('overseas')로 취급한다.
 * (apps/main의 normalizeFlightRouteType와 동일한 정책)
 */
const normalizeFlightRouteType = (search: string) => {
  const parsed = qs.parse(search, { ignoreQueryPrefix: true }).routeType;
  const raw = Array.isArray(parsed) ? parsed[0] : parsed;

  return typeof raw === 'string' && raw.toLowerCase() === 'domestic' ? 'domestic' : 'overseas';
};

const isIncludeMenuQuery = (
  menuUrlQueryString: string,
  currentPathQueryString: string,
  targetQueries: string[] = [],
) => {
  try {
    const parsedMenuQuery = qs.parse(menuUrlQueryString, {
      ignoreQueryPrefix: true,
    });
    const parsedCurrentQuery = qs.parse(currentPathQueryString, {
      ignoreQueryPrefix: true,
    });

    if (targetQueries?.length) {
      return targetQueries.every((query) => parsedMenuQuery[query] === parsedCurrentQuery[query]);
    } else {
      const entriesParsedMenuQuery = Object.entries(parsedMenuQuery);
      return entriesParsedMenuQuery.every(([key, value]) => parsedCurrentQuery[key] === value);
    }
  } catch (err) {
    return false;
  }
};

const isExactMatchedPath = ({
  menuPathName,
  menuSearch,
  currentPathName,
  currentSearch,
  exactMatchedPath,
}: {
  menuPathName: string;
  menuSearch: string;
  currentPathName: string;
  currentSearch: string;
  exactMatchedPath: ExactMatchedPath;
}) => {
  // ex) menuPathName: /promotions, currentPathName: /promotions/rentacar_coupon
  // ex) menuPathName: /accommodations, currentPathName: /accommodations/stays
  const pattern = menuPathName.replace(/\/$/g, '');
  const regexp = new RegExp(`^${pattern}[/]?$`, 'g');
  const { ignoreQuery } = exactMatchedPath;
  const isExactMatched = regexp.test(currentPathName);

  if (!ignoreQuery) {
    const [, parsedMenuQuery] = menuSearch.split('?');
    const isIncludeQuery = isIncludeMenuQuery(parsedMenuQuery, currentSearch);

    return isIncludeQuery && isExactMatched;
  }

  return isExactMatched;
};

const parseMenuUrl = (menuUrl: string) => {
  try {
    const { hostname, pathname, search } = new URL(menuUrl);

    return { menuPathName: pathname, menuSearch: search, menuHostName: hostname };
  } catch (error) {
    return null;
  }
};

export const isActiveMenu = ({
  code,
  menuUrl,
  isFlightMode,
}: {
  code: string;
  menuUrl: string;
  isFlightMode: boolean;
}) => {
  // 항공 서비스에서 동작하는 경우 항공권 메뉴 아이템 활성화 처리
  if (isFlightMode) {
    return code === 'flights';
  }

  // "홈" 매뉴의 경우 비활성화 처리
  if (code === 'home') {
    return false;
  }

  const { menuPathName = '', menuSearch = '', menuHostName = '' } = parseMenuUrl(menuUrl) ?? {};
  const {
    pathname: currentPathName = '',
    search: currentSearch = '',
    hostname: currentHostName = '',
  } = typeof window !== 'undefined' ? window.location : {};

  /** www.myrealtrip.com/union/.... 대응코드 */
  if (
    currentHostName.split('.')[0] === 'www' &&
    currentPathName.startsWith('/union') &&
    menuPathName.startsWith('/union')
  ) {
    return isIncludeMenuQuery(menuSearch, currentSearch, ['isDomestic']);
  }

  /** www.myrealtrip.com/main/union 대응코드 */
  if (
    currentHostName.split('.')[0] === 'www' &&
    currentPathName.startsWith('/main/unionstay') &&
    menuPathName.startsWith('/main/unionstay')
  ) {
    return isIncludeMenuQuery(menuSearch, currentSearch, ['stayType']);
  }

  /** www.myrealtrip.com/main/flights 대응코드 (routeType으로 국내/해외 항공 구분) */
  if (
    currentHostName.split('.')[0] === 'www' &&
    currentPathName.startsWith('/main/flights') &&
    menuPathName.startsWith('/main/flights')
  ) {
    return normalizeFlightRouteType(menuSearch) === normalizeFlightRouteType(currentSearch);
  }

  const isMatchedHost = menuHostName === currentHostName;
  if (!isMatchedHost) {
    return false;
  }

  const currentSubDomain = currentHostName.split('.')[0];

  // 서브 도메인을 사용하는 경우, query string 까지 체크할지 여부 판단
  if (ALLOW_SUB_DOMAINS.includes(currentSubDomain)) {
    return REQUIRED_SUB_DOMAINS_EXACT_MATCHING_PATH_LIST.some(
      ({ subDomain, ignoreQuery, queries }) => {
        const isSubDomainMatched = subDomain === currentSubDomain;

        return (
          isSubDomainMatched &&
          (ignoreQuery || isIncludeMenuQuery(menuSearch, currentSearch, queries))
        );
      },
    );
  }

  // accommodations는 숙소와 국내펜션의 path가 중복된다
  // promotion는 프로모션 목록, 하위 페이지와 path가 중복되지 않도록 처리한다.
  const exactMatchedPath = REQUIRED_EXACT_MATCHING_PATH_LIST.find(
    ({ path }) => menuPathName.startsWith(path) && currentPathName.startsWith(path),
  );

  if (exactMatchedPath) {
    return isExactMatchedPath({
      menuPathName,
      menuSearch,
      currentPathName,
      currentSearch,
      exactMatchedPath,
    });
  }

  return currentPathName.startsWith(menuPathName);
};
